const MenuModal = ({
  open,
  onClose,
  onSave,
  editableMenu,
  setEditableMenu,
  categories = [],
  isAdding,
}) => {
  if (!open || !editableMenu) return null;

  const addSplitPack = () => {
    setEditableMenu((prev) => {
      const splitMenu = prev.splitMenu || {};

      const qtys = Object.keys(splitMenu)
        .map(Number)
        .sort((a, b) => a - b);

      let nextQty = 1;

      // find first missing sequential number
      for (let i = 0; i < qtys.length; i++) {
        if (qtys[i] !== nextQty) break;
        nextQty++;
      }

      return {
        ...prev,
        splitMenu: {
          ...splitMenu,
          [nextQty]: "",
        },
      };
    });
  };

  const removeSplitPack = (qty) => {
    setEditableMenu((prev) => {
      const updated = { ...prev.splitMenu };
      delete updated[qty];

      return {
        ...prev,
        splitMenu: updated,
      };
    });
  };

  const hasAtLeastOnePrice = Object.values(editableMenu.splitMenu || {}).some(
    (v) => Number(v) > 0,
  );

  const hasEmptySplitPrice = Object.values(editableMenu.splitMenu || {}).some(
    (v) => String(v ?? "").trim() === "",
  );

  const hasNonPositiveSplitPrice = Object.values(
    editableMenu.splitMenu || {},
  ).some((v) => {
    const value = String(v ?? "").trim();
    if (value === "") return false;

    return Number(value) <= 0 || Number.isNaN(Number(value));
  });

  const hasTooLargeSplitPrice = Object.values(editableMenu.splitMenu || {}).some(
    (v) => {
      const value = String(v ?? "").trim();
      if (value === "") return false;

      return Number(value) > 999;
    },
  );

  let splitPriceErrorMessage = "";
  if (hasEmptySplitPrice) {
    splitPriceErrorMessage = "Please add price for all added quantities";
  } else if (hasNonPositiveSplitPrice) {
    splitPriceErrorMessage = "Price must be greater than 0";
  } else if (hasTooLargeSplitPrice) {
    splitPriceErrorMessage = "Price cannot be greater than 999";
  } else if (!hasAtLeastOnePrice) {
    splitPriceErrorMessage = "At least one price is required";
  }

  const isStockInvalid = editableMenu.isInStock && !editableMenu.inStock;

  const isCategoryInvalid = !String(editableMenu.category ?? "").trim();

  const isSaveDisabled =
    Boolean(splitPriceErrorMessage) || isStockInvalid || isCategoryInvalid;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white w-full max-w-md rounded-xl shadow-lg relative flex flex-col max-h-[85vh]">
        <div className="flex justify-between items-baseline p-3 border-b">
          <h3 className="text-lg font-semibold">
            {isAdding ? "Add New Menu" : "Edit Menu Item"}
          </h3>

          <button
            onClick={onClose}
            className="text-gray-400 border-0 hover:text-gray-600 text-xl"
          >
            ×
          </button>
        </div>

        <div className="px-3 overflow-y-auto space-y-3 flex-1">
          <label className="text-sm font-semibold -mb-2">Item Name</label>
          <input
            type="text"
            placeholder="Item name"
            className="w-full border rounded-lg px-3 py-2 text-sm"
            value={editableMenu.itemName}
            onChange={(e) =>
              setEditableMenu({
                ...editableMenu,
                itemName: e.target.value,
              })
            }
          />

          <label className="text-sm font-semibold -mb-2">Item Number</label>
          <input
            type="number"
            min={1}
            max={99}
            placeholder="Assign a display number (1-99)"
            className="w-full border rounded-lg px-3 py-2 text-sm"
            value={editableMenu.itemNumber || ""}
            onChange={(e) =>
              setEditableMenu({
                ...editableMenu,
                itemNumber: e.target.value ? Number(e.target.value) : "",
              })
            }
          />
          <label className="text-sm font-semibold -mb-2">Description</label>
          <textarea
            rows={3}
            placeholder="Description"
            className="w-full border rounded-lg px-3 py-2 text-sm"
            value={editableMenu.itemDescription}
            onChange={(e) =>
              setEditableMenu({
                ...editableMenu,
                itemDescription: e.target.value,
              })
            }
          />
          <div className="border rounded-lg p-3">
            <p className="text-sm font-medium mb-2">Split Menu Pricing</p>

            {Object.entries(editableMenu.splitMenu || {}).map(
              ([qty, price]) => (
                <div key={qty} className="flex items-center gap-2 mb-2">
                  <input
                    type="number"
                    className="w-20 border rounded-lg px-2 py-1 text-sm bg-gray-100"
                    value={qty}
                    disabled
                  />

                  <input
                    type="number"
                    placeholder="Price"
                    min={0}
                    max={999}
                    className="flex-1 border rounded-lg px-3 py-2 text-sm"
                    value={price}
                    onChange={(e) =>
                      setEditableMenu((prev) => ({
                        ...prev,
                        splitMenu: {
                          ...prev.splitMenu,
                          [qty]: e.target.value,
                        },
                      }))
                    }
                  />

                  <button
                    type="button"
                    onClick={() => removeSplitPack(qty)}
                    className="text-red-500 text-lg px-2"
                  >
                    ×
                  </button>
                </div>
              ),
            )}

            <button
              type="button"
              onClick={addSplitPack}
              className="text-sm font-medium p-2 rounded-md shadow-md text-blue-600 border-0 mt-1 hover:underline"
            >
              + Add Pack
            </button>

            {splitPriceErrorMessage && (
              <p className="text-xs text-red-500 mt-1">
                {splitPriceErrorMessage}
              </p>
            )}
          </div>

          <div className="border rounded-lg p-3">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                checked={editableMenu.isInStock || false}
                onChange={(e) =>
                  setEditableMenu({
                    ...editableMenu,
                    isInStock: e.target.checked,
                    inStock: e.target.checked
                      ? editableMenu.inStock || ""
                      : undefined,
                  })
                }
              />
              Enable Stock Limit
            </label>

            {editableMenu.isInStock && (
              <input
                type="number"
                placeholder="Available quantity"
                className="w-full border rounded-lg px-3 py-2 text-sm mt-2"
                value={editableMenu.inStock || ""}
                onChange={(e) =>
                  setEditableMenu({
                    ...editableMenu,
                    inStock: e.target.value,
                  })
                }
              />
            )}

            {isStockInvalid && (
              <p className="text-xs text-red-500 mt-1">
                Stock quantity is required
              </p>
            )}
          </div>
          <label className="text-sm font-semibold -mb-2">
            Preparation Time
          </label>

          <input
            type="number"
            placeholder="Preparation time (mins)"
            className="w-full border rounded-lg px-3 py-2 text-sm"
            value={editableMenu.prepTime || ""}
            onChange={(e) =>
              setEditableMenu({
                ...editableMenu,
                prepTime: e.target.value,
              })
            }
          />
          <label className="text-sm font-semibold -mb-2">Item Type</label>

          <select
            className="w-full border rounded-lg px-3 py-2 text-sm"
            value={editableMenu.itemType}
            onChange={(e) =>
              setEditableMenu({
                ...editableMenu,
                itemType: e.target.value,
              })
            }
          >
            <option value="Vegetarian">Vegetarian</option>
            <option value="Non-Vegetarian">Non-Vegetarian</option>
          </select>
          <label className="text-sm font-semibold -mb-2">Category</label>

          <select
            className="w-full border rounded-lg px-3 py-2 text-sm"
            value={editableMenu.category || ""}
            onChange={(e) =>
              setEditableMenu({
                ...editableMenu,
                category: e.target.value,
              })
            }
            required
          >
            <option value="">Select category</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
          {isCategoryInvalid && (
            <p className="text-xs text-red-500 mt-1">Category is required</p>
          )}
        </div>
        <div className="p-3 border-t flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm shadow-md rounded-lg border-0 hover:bg-gray-100"
          >
            Cancel
          </button>

          <button
            disabled={isSaveDisabled}
            onClick={onSave}
            className={`px-5 py-2 text-sm rounded-lg shadow-md border-0 text-white ${
              isSaveDisabled
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-green-600 hover:bg-green-700"
            }`}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default MenuModal;
